import {
    ChangeEvent,
    FocusEvent,
    ReactElement,
    ReactNode,
    Ref,
    forwardRef,
    useEffect,
    useMemo,
} from 'react';
import { Validate, useFormContext } from 'react-hook-form';

import {
    TextFieldProps as MUITextFieldProps,
    InputAdornment as MaterialUIInputAdornment,
    TextField as MaterialUITextField,
} from '@mui/material';
import { clsx } from 'clsx';

import './TextField.scss';

export interface BaseTextFieldProps {
    inputRef?: any;
    name: string;
    label?: string | ReactElement;
    placeholder?: string;
    value?: string;
    defaultValue?: string;
    type?: 'text' | 'number' | 'email' | 'password' | 'search' | 'tel' | 'url';
    inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    multiline?: boolean;
    rows?: number;
    className?: string;
    color?: 'neutral' | 'blue' | 'green' | 'red';
    variant?: 'outlined' | 'raised' | 'smooth' | 'filled';
    autoFocus?: boolean;
    maxLength?: number;
    autoComplete?: 'new-password' | 'on' | 'off';
    'data-testid'?: string;
    min?: number;
    max?: number;
    step?: number;
    validate?: Validate;
    disableFieldRegister?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onMouseDown?: (e: React.MouseEvent<HTMLInputElement>) => void;
    onValidationError?: (value?: string) => void;
}

const AdornmentWrapper = ({
    position,
    children,
}: {
    position: 'start' | 'end';
    children: ReactNode;
}) => <MaterialUIInputAdornment position={position}>{children}</MaterialUIInputAdornment>;

export const BaseTextField = forwardRef<HTMLInputElement, BaseTextFieldProps>(
    (
        {
            inputRef,
            name,
            label,
            placeholder,
            value,
            defaultValue,
            type = 'text',
            inputMode,
            required = false,
            disabled = false,
            error = false,
            helperText,
            startIcon,
            endIcon,
            multiline = false,
            rows,
            className,
            color = 'neutral',
            variant = 'outlined',
            autoFocus = false,
            maxLength,
            autoComplete = 'off',
            'data-testid': dataTestId,
            min,
            max,
            step,
            validate,
            disableFieldRegister = false,
            onChange,
            onBlur,
            onFocus,
            onKeyDown,
            onMouseDown,
            onValidationError,
        },
        ref,
    ) => {
        const formContext = useFormContext();
        const {
            errors,
            register,
            formState: { isSubmitSuccessful = true, submitCount = 0 },
            getValues,
        } = formContext || {
            errors: {},
            register: null,
            formState: {},
            getValues: () => {},
        };

        const props: MUITextFieldProps = {
            type,
            onChange,
            onKeyDown,
            onBlur,
            onFocus,
            onMouseDown,
            label,
            name,
            placeholder,
            value,
            defaultValue,
            required,
            multiline,
            rows,
            disabled,
            helperText,
            InputLabelProps: { shrink: true },
            inputRef,
            inputProps: {
                autoComplete,
                inputMode,
                onWheel: type === 'number' ? (event: any) => event.currentTarget.blur() : undefined,
                'data-testid': dataTestId ?? `textField.${name}`,
                min,
                max,
                maxLength,
                step,
            },
            autoFocus,
        };

        // TODO - for now this is same as text field. Mostly because normal way is not working in the spark-wizard since the error validation is tricky.
        if (register != null) {
            if (inputRef == null) {
                props.inputRef = register({
                    required,
                    min,
                    max,
                    validate,
                });
            } else {
                if (!disableFieldRegister) {
                    if (typeof inputRef === 'function') {
                        props.inputRef = (instance: any) =>
                            register(inputRef(instance), {
                                required,
                                min,
                                max,
                                validate,
                            });
                    } else {
                        // TODO: Does not work. Fix TS error overridden by `as` and debug
                        props.inputRef = register(inputRef, {
                            required,
                            min,
                            max,
                            validate,
                        }) as unknown as Ref<any>;
                    }
                }
            }
        }

        // Handle start/end icons
        if (startIcon) {
            props.InputProps = {
                startAdornment: <AdornmentWrapper position="start">{startIcon}</AdornmentWrapper>,
            };
        }

        if (endIcon) {
            props.InputProps = {
                ...props.InputProps,
                endAdornment: <AdornmentWrapper position="end">{endIcon}</AdornmentWrapper>,
            };
        }

        // Calculate field error exactly like original
        const fieldError = useMemo<boolean>(() => {
            if (error) {
                return true;
            }
            return !!(name && errors[name]);
        }, [error, errors, name]);

        useEffect(() => {
            if (!isSubmitSuccessful && fieldError && onValidationError) {
                onValidationError(getValues(name));
            }
        }, [submitCount, isSubmitSuccessful, fieldError, onValidationError, getValues, name]);

        return (
            <MaterialUITextField
                className={clsx(
                    className,
                    'form-control',
                    'text-field',
                    `text-field-color-${color}`,
                    `text-field-variant-${variant}`,
                    fieldError ? 'text-field-error' : '',
                )}
                error={fieldError}
                {...props}
            />
        );
    },
);

BaseTextField.displayName = 'BaseTextField';

export default BaseTextField;
